import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { seoConfig, getPageSEO, getCanonicalUrl } from '../../config/seo';

const SEO = ({
  title,
  description,
  keywords,
  image,
  type = 'website',
  pageName,
  noindex = false,
  nofollow = false,
  jsonLd
}) => {
  const location = useLocation();

  // Get page-specific SEO if pageName is provided
  const pageSEO = pageName ? getPageSEO(pageName) : null;

  // Use provided values or fall back to page-specific or default values
  const seoTitle = title || pageSEO?.title || seoConfig.defaultTitle;
  const seoDescription = description || pageSEO?.description || seoConfig.defaultDescription;
  const seoKeywords = keywords || pageSEO?.keywords || '';
  const seoImage = image || seoConfig.defaultImage;

  // Generate canonical URL (no language prefix — site is English-only)
  const canonicalUrl = getCanonicalUrl(location.pathname);

  // Full image URL
  const fullImageUrl = seoImage.startsWith('http')
    ? seoImage
    : `${seoConfig.siteUrl}${seoImage}`;

  // Robots meta
  const robotsContent = [
    noindex ? 'noindex' : 'index',
    nofollow ? 'nofollow' : 'follow'
  ].join(', ');

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      {seoKeywords && <meta name="keywords" content={seoKeywords} />}
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Language — English only */}
      <html lang="en" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="DELVANTE" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={seoTitle} />
      <meta property="twitter:description" content={seoDescription} />
      <meta property="twitter:image" content={fullImageUrl} />
      {seoConfig.twitterHandle && (
        <meta property="twitter:creator" content={seoConfig.twitterHandle} />
      )}

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}

      {/* Organization Schema (on all pages) */}
      <script type="application/ld+json">
        {JSON.stringify(seoConfig.organization)}
      </script>
    </Helmet>
  );
};

export default SEO;
